import { get, set } from 'lodash';
import { all, call } from 'redux-saga/effects';

import { FIELD_PATHS, FIELD_VALUES, NO_RELATIONSHIP } from '@funnels/constants';
import { StoreApplicant } from '@funnels/types';

import { getApplicantId } from '../_common';

export function mapApplicantRelationshipOut(applicant: StoreApplicant) {
  const partnerId = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_MARITAL_DETAILS_APPLICANT_ID(0));
  const isNonApplicant = partnerId === '';

  if (isNonApplicant) {
    set({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_MARITAL_DETAILS(0), undefined);
  }
}

export function mapApplicantRelationshipIn(applicant: StoreApplicant) {
  const maritalStatus = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_MARITAL_STATUS(0));
  const partnerId = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_MARITAL_DETAILS_APPLICANT_ID(0));
  const isNonApplicant = maritalStatus && !NO_RELATIONSHIP.includes(maritalStatus) && !partnerId;

  if (isNonApplicant) {
    set({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_MARITAL_DETAILS_APPLICANT_ID(0), '');
  }
}

export function mapApplicantDependantsOut(applicant: StoreApplicant) {
  const dependants = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_DEPENDANTS(0), []);

  dependants.forEach((__: any, index: number) => {
    const guardianId = get(
      { applicants: [applicant] },
      FIELD_PATHS.APPLICANT_DEPENDANTS_SHARE_WITH_APPLICANT_ID(0, index)
    );
    const isNonApplicant = guardianId === '';

    if (isNonApplicant) {
      set({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_DEPENDANTS_SHARE_WITH(0, index), undefined);
    }
  });
}

export function mapApplicantDependantsIn(applicant: StoreApplicant) {
  const dependants = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_DEPENDANTS(0), []);

  dependants.forEach((__: any, index: number) => {
    const sharedWith = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_DEPENDANTS_SHARE_WITH(0, index));
    const isNonApplicant = !sharedWith;

    if (isNonApplicant) {
      set({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_DEPENDANTS_SHARE_WITH_APPLICANT_ID(0, index), '');
    }
  });
}

export function* fixMaritalRelationship(applicant: StoreApplicant, applicants: StoreApplicant[]): any {
  const partnerClientId = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_MARITAL_DETAILS_APPLICANT_ID(0));

  // doesn't have a partner
  if (!partnerClientId) {
    return;
  }

  const partner = applicants.find((a) => a.clientId === partnerClientId);

  if (!partner) {
    return;
  }

  // wait until we get the partner's (server) id.
  const partnerId = yield getApplicantId(partnerClientId, partner.id);

  // hydrated applicants have the same value for both id/clientId. It is therefore not necessary to do the mapping.
  // this only applies/fixes (server) id-less applicants who have been created but not hydrated (in the form state).
  if (partnerId && partnerId !== partnerClientId) {
    set({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_MARITAL_DETAILS_APPLICANT_ID(0), partnerId);
  }
}

function* fixDepedantSharedWith(dependantIndex: number, applicant: StoreApplicant, applicants: StoreApplicant[]): any {
  const guardianClientId = get(
    { applicants: [applicant] },
    FIELD_PATHS.APPLICANT_DEPENDANTS_SHARE_WITH_APPLICANT_ID(0, dependantIndex)
  );

  if (!guardianClientId) {
    return;
  }

  const guardian = applicants.find((a) => a.clientId === guardianClientId);

  if (!guardian) {
    return;
  }

  const guardianId = yield getApplicantId(guardianClientId, guardian.id);

  if (guardianId && guardianId !== guardianClientId) {
    set(
      { applicants: [applicant] },
      FIELD_PATHS.APPLICANT_DEPENDANTS_SHARE_WITH_APPLICANT_ID(0, dependantIndex),
      guardianId
    );
  }
}

export function* fixDependantsSharedWith(applicant: StoreApplicant, applicants: StoreApplicant[]) {
  const dependants = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_DEPENDANTS(0), []);

  yield all(dependants.map((__: any, index: number) => call(fixDepedantSharedWith, index, applicant, applicants)));
}

export function fixApplicantDefaultValues(applicant: StoreApplicant) {
  const applicantType = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_TYPE(0));

  if (!applicantType) {
    set({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_TYPE(0), FIELD_VALUES.BORROWER);
  }
}

export function trimApplicantSpaces(applicant: StoreApplicant) {
  const applicantFirstName = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_FIRST_NAME(0));
  const applicantMiddleName = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_MIDDLE_NAME(0));
  const applicantLastName = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_LAST_NAME(0));

  set({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_FIRST_NAME(0), applicantFirstName?.trim());
  set({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_MIDDLE_NAME(0), applicantMiddleName?.trim());
  set({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_LAST_NAME(0), applicantLastName?.trim());
}
